/** Explorer Results view Evo */
import {useAuth} from '@apps-common/auth-react';
import * as searchesApi from '@apps-common/js-client/api/searches';
import DailyImageryIcon from '@apps-common/ui-icons/DailyImagery.jsx';
import {Stack} from '@mui/material';
import React, {Suspense, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {notifyError} from '@components/notifier/notify.tsx';
import WorkerManager from '@components/worker-manager';
import {loadSearch, searchSingleItem} from '@src/actions/search';
import {setAccessPermissions} from '@src/actions/search-filters';
import {trackEvent} from '@src/analytics';
import {getItem as getItemRequest} from '@src/api/items';
import {SidePanel} from '@src/components/panels/SidePanels.tsx';
import {hydrateSearchGeometry} from '@src/utils/graneroResolver.js';
import pluckQueryParam from '@src/utils/pluckQueryParam.js';
import {isTrialUser} from '@src/utils/user.js';

const FiltersPanel = React.lazy(
  () => import('@components/panels/search/filters/FiltersPanel.jsx'),
);

const SearchPanel = React.lazy(
  () => import('@components/panels/search/SearchPanel.jsx'),
);

const Search = () => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const showFiltersPanel = useSelector(state => state.sidebar.showFiltersPanel);

  const getItem = query => {
    const [itemType, itemId] = query.split('::');

    if (!itemType || !itemId) {
      return Promise.resolve(null);
    }

    // we intentionally don't care about bad requests here
    return getItemRequest({itemType, itemId}).catch(() => null);
  };

  useEffect(() => {
    const searchId = pluckQueryParam('search');
    if (searchId) {
      trackEvent('Loaded a saved search from link', {searchId});

      const fetchSearch = async () => {
        try {
          const search = await searchesApi.get(searchId);
          const hydratedSearch = await hydrateSearchGeometry(search);
          dispatch(loadSearch(hydratedSearch));
        } catch (error) {
          notifyError({
            data: {
              message:
                'There was an error while loading the requested saved search',
            },
          });
        }
      };

      fetchSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const queryItemId = pluckQueryParam('item');
    if (queryItemId) {
      const itemId = queryItemId.split(',')[0];
      getItem(itemId).then(item => {
        if (item) {
          dispatch(searchSingleItem(item));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setAccessPermissions(!isTrialUser(auth.user.claims)));
  }, [auth.user.claims, dispatch]);

  return (
    <>
      <WorkerManager />
      <Stack
        sx={{
          display: showFiltersPanel ? 'flex' : 'none',
          height: '100%',
        }}
      >
        <Suspense>
          <FiltersPanel />
        </Suspense>
      </Stack>

      <Stack
        sx={{
          display: !showFiltersPanel ? 'flex' : 'none',
          height: '100%',
        }}
      >
        <SidePanel
          title="Daily results"
          headerProps={{
            icon: <DailyImageryIcon color="action" />,
          }}
        >
          <SearchPanel />
        </SidePanel>
      </Stack>
    </>
  );
};

export default Search;
